import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, ArrowRight, Sparkles } from 'lucide-react';
import { getCategoryClassroomUrl } from './wellnessAssessmentData';

const API = process.env.REACT_APP_BACKEND_URL;

const PILLAR_COLORS = {
  radiant_body: 'from-burgundy to-burgundy/80',
  balanced_mind: 'from-dark-teal to-dark-teal/80',
  enduring_vitality: 'from-gold to-gold/80'
};

const PersonalizedWelcome = ({ user, onNavigate }) => {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user?.id) fetchRecommendations();
  }, [user?.id, user?.wellness_assessment]);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/api/users/${user.id}/recommended-content`);
      setRecommendations(response.data);
    } catch (error) {
      console.log('Could not fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || dismissed || !recommendations?.has_assessment) return null;

  const firstName = user?.name?.split(' ')[0] || 'there';
  const pillarGradient = PILLAR_COLORS[recommendations.primary_pillar] || 'from-burgundy to-dark-teal';
  const articles = recommendations.recommendations || [];

  return (
    <div className="bg-white rounded-xl border border-burgundy/10 shadow-sm overflow-hidden" data-testid="personalized-welcome">
      {/* Header */}
      <div className={`bg-gradient-to-r ${pillarGradient} px-5 py-4`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif font-bold text-cream text-lg">
              Welcome back, {firstName}
            </h3>
            <p className="text-white text-xs mt-0.5">
              Your focus: {recommendations.primary_pillar_label}
            </p>
          </div>
          <Sparkles className="h-5 w-5 text-cream/60" />
        </div>
      </div>

      {/* Recommended Content */}
      {articles.length > 0 && (
        <div className="px-5 py-4">
          <p className="text-burgundy/60 text-xs font-medium mb-3 uppercase tracking-wider">Recommended for You</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {articles.slice(0, 3).map((article, i) => (
              <button
                key={article.id || i}
                onClick={() => onNavigate('classroom', article)}
                className="flex items-start gap-3 p-3 rounded-lg border border-burgundy/10 hover:border-burgundy/25 hover:bg-cream/50 transition-all text-left group"
                data-testid={`recommended-article-${i}`}
              >
                {article.image_url ? (
                  <img
                    src={article.image_url}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-burgundy/5 flex items-center justify-center shrink-0">
                    <BookOpen className="h-5 w-5 text-burgundy/30" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-burgundy line-clamp-2 group-hover:text-burgundy/80">
                    {article.title}
                  </p>
                  {article.subcategory && (
                    <p className="text-[10px] text-burgundy/40 mt-1 truncate">{article.subcategory}</p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Subcategory Quick Links */}
          {recommendations.top_subcategories?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-burgundy/5 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-burgundy/40 font-medium">Your topics:</span>
              {recommendations.top_subcategories.slice(0, 4).map(subcat => (
                <button
                  key={subcat}
                  onClick={() => {
                    const url = getCategoryClassroomUrl(subcat);
                    onNavigate('classroom-url', url);
                  }}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-burgundy/5 text-burgundy/60 hover:bg-burgundy/10 hover:text-burgundy transition-colors"
                >
                  {subcat}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {articles.length === 0 && recommendations.top_subcategories?.length > 0 && (
        <div className="px-5 py-4">
          <p className="text-burgundy/60 text-sm mb-3">Based on your assessment, explore:</p>
          <div className="flex flex-wrap gap-2">
            {recommendations.top_subcategories.map(subcat => (
              <button
                key={subcat}
                onClick={() => {
                  const url = getCategoryClassroomUrl(subcat);
                  onNavigate('classroom-url', url);
                }}
                className="px-3 py-1.5 rounded-full bg-burgundy/5 text-burgundy text-xs font-medium hover:bg-burgundy/10 transition-colors inline-flex items-center gap-1"
              >
                {subcat} <ArrowRight className="h-3 w-3" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalizedWelcome;
